import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import useCoreSession, { coreErrorMessage, moneyAmount } from '../../stores/coreSession';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = { loading: false, error: null, notice: '' };

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

function snapshot() {
  const session = useCoreSession.getState();
  const core = session.state || {};
  return {
    status: session.status,
    heat: Number(core.heat ?? 0),
    cash: moneyAmount(core.cash),
    district: core.districtId || '--',
  };
}

function riskBand(heat) {
  if (heat >= 700) return { label: 'SEIZED', color: TERMINAL.TEXT_BRIGHT };
  if (heat >= 400) return { label: 'WARNING', color: TERMINAL.TEXT };
  if (heat >= 150) return { label: 'WATCH', color: TERMINAL.GREEN };
  return { label: 'CLEAR', color: TERMINAL.TEXT_DIM };
}

function drawTop(ctx, width) {
  const core = snapshot();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // AUDITOR', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, 'SYSTEMIC AUDIT // HEAT', 232, 5, TERMINAL.TEXT_DIM);
  text(ctx, core.status === 'online' ? 'LIVE' : 'OFFLINE', width - 64, 5, core.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawHeat(ctx, x, y, w, h) {
  const core = snapshot();
  const band = riskBand(core.heat);
  DopeUI.Panel(ctx, x, y, w, h, 'AUDIT RISK');
  text(ctx, 'HEAT', x + 12, y + 30, TERMINAL.TEXT_DIM);
  text(ctx, `${core.heat}/1000`, x + 120, y + 30, TERMINAL.TEXT_BRIGHT);
  const barX = x + 12;
  const barY = y + 52;
  const barW = w - 24;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(barX, barY, barW, 10);
  ctx.strokeStyle = TERMINAL.GREEN_DARK;
  ctx.strokeRect(barX, barY, barW, 10);
  ctx.fillStyle = band.color;
  ctx.fillRect(barX + 1, barY + 1, Math.max(0, (barW - 2) * Math.min(1000, core.heat) / 1000), 8);
  text(ctx, 'BAND', x + 12, y + 76, TERMINAL.TEXT_DIM);
  text(ctx, band.label, x + 120, y + 76, band.color);
  text(ctx, 'THRESHOLDS', x + 12, y + 96, TERMINAL.TEXT_DIM);
  text(ctx, '150 WATCH / 400 WARN / 700 SEIZE', x + 12, y + 114, TERMINAL.TEXT_DIM);
  text(ctx, 'STATE', x + 12, y + 138, TERMINAL.TEXT_DIM);
  text(ctx, core.status === 'online' ? 'AUDIT OPEN' : 'OFFLINE', x + 120, y + 138, core.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawAction(ctx, x, y, w, h) {
  const core = snapshot();
  DopeUI.Panel(ctx, x, y, w, h, 'BRIBE // AUDITOR');
  text(ctx, 'CASH', x + 12, y + 30, TERMINAL.TEXT_DIM);
  text(ctx, core.status === 'online' ? `$${core.cash.toFixed(2)}` : '--', x + 120, y + 30, TERMINAL.TEXT);
  text(ctx, 'DISTRICT', x + 12, y + 50, TERMINAL.TEXT_DIM);
  text(ctx, String(core.district).toUpperCase(), x + 120, y + 50, TERMINAL.TEXT);
  text(ctx, 'COST', x + 12, y + 70, TERMINAL.TEXT_DIM);
  text(ctx, 'PRICE', x + 120, y + 70, TERMINAL.TEXT_DIM);
  text(ctx, 'EFFECT', x + 12, y + 90, TERMINAL.TEXT_DIM);
  text(ctx, 'HEAT REDUCED', x + 120, y + 90, TERMINAL.TEXT);
  rule(ctx, x + 12, y + 112, w - 24);
  const message = state.error
    || state.notice
    || (core.heat <= 0 ? 'NO HEAT TO BRIBE.' : 'OFFICER WAITING ON YOU.');
  text(ctx, message.slice(0, 38), x + 12, y + 126, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);

  const canBribe = core.status === 'online' && core.heat > 0 && !state.loading;
  const actionY = y + h - 38;
  DopeUI.Button(ctx, x + 12, actionY, w - 24, 24, state.loading ? 'SENDING' : 'BRIBE AUDITOR', canBribe ? 'hover' : 'disabled');
  if (canBribe) {
    interactiveRegistry.register({
      id: 'audit-bribe',
      x: x + 12,
      y: actionY,
      w: w - 24,
      h: 24,
      onClick: async () => {
        state.loading = true;
        state.error = null;
        try {
          const result = await useCoreSession.getState().bribeHeat();
          state.notice = `HEAT ${result.bribe?.heatAfter ?? '--'} // PAID ${result.bribe?.cost?.amount ?? '--'}`;
        } catch (error) {
          state.error = coreErrorMessage(error);
        } finally {
          state.loading = false;
        }
      },
    });
  }
}

registerView('audit', function renderAudit(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  drawTop(ctx, safe.width);
  const mobile = safe.width < 500;
  if (mobile) {
    drawHeat(ctx, 8, 32, safe.width - 16, 250);
    drawAction(ctx, 8, 290, safe.width - 16, 210);
  } else {
    drawHeat(ctx, 8, 36, 376, 280);
    drawAction(ctx, 400, 36, 232, 280);
  }

  const footerY = mobile ? 512 : 328;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER   ESC DESKTOP', 8, mobile ? footerY + 8 : 360, TERMINAL.TEXT_DIM);
  const backX = mobile ? 8 : safe.width - 200;
  const backY = mobile ? 600 : 336;
  const backW = mobile ? safe.width - 16 : 192;
  DopeUI.Button(ctx, backX, backY, backW, 24, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'audit-back', x: backX, y: backY, w: backW, h: 24,
    onClick: () => navigateTo('main_menu'),
  });
  if (!mobile) text(ctx, 'AUDITOR // HEAT IS A DEBT.', 8, 384, TERMINAL.GREEN);
});
